/** Breadcrumbs component showing user's current location in app hierarchy. */
import React from "react";
import { Breadcrumbs as MuiBreadcrumbs, Link, Typography, Box } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import { useNavigate, useLocation } from "react-router-dom";

/** Breadcrumb item structure. */
export interface BreadcrumbItem {
  text: string;
  path?: string; // undefined for current page (no link)
}

/** Props for Breadcrumbs component. */
export interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
}

/** Route to breadcrumb label mapping for special routes. */
const ROUTE_LABELS: Record<string, string> = {
  "/": "Dashboard",
  "/accounts": "Accounts",
  "/resellers": "Resellers",
  "/wholesale": "Wholesale Clients",
  "/pin-customers": "PIN Customers",
  "/invoices": "Invoices",
  "/payments": "Payments",
  "/rate-plans": "Rate Plans",
  "/tax-reports": "Tax Reports",
  "/carriers": "Carriers",
  "/suppliers": "Suppliers",
  "/gateways": "Gateways",
  "/routing": "Routing",
  "/cdrs": "CDRs",
  "/traffic": "Traffic Analysis",
  "/financial-reports": "Financial Reports",
  "/cgrates/status": "Billing Engine Status",
  "/api-keys": "API Keys",
  "/alerts": "Alerts",
  "/audit": "Audit Log",
  "/business-kpis": "Business KPIs",
  "/billing": "Billing",
};

/** Group to parent mapping for nested routes. */
const GROUP_PARENTS: Record<string, string> = {
  "/accounts": "Customers",
  "/resellers": "Customers",
  "/wholesale": "Customers",
  "/pin-customers": "Customers",
  "/invoices": "Billing & Finance",
  "/payments": "Billing & Finance",
  "/rate-plans": "Billing & Finance",
  "/tax-reports": "Billing & Finance",
  "/carriers": "Routing",
  "/suppliers": "Routing",
  "/gateways": "Routing",
  "/lcr": "Routing",
  "/cdrs": "Reports",
  "/traffic": "Reports",
  "/financial-reports": "Reports",
  "/cgrates/status": "System",
  "/api-keys": "System",
  "/alerts": "System",
  "/audit": "System",
  "/business-kpis": "Overview",
  "/billing": "Billing & Finance",
  "/rates/import": "Routing",
};

/** Generate breadcrumb items from current path. */
function generateBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [];

  // Always start with home
  items.push({ text: "Home", path: "/" });

  // If we're on the dashboard, we're done
  if (pathname === "/" || pathname === "") {
    return items;
  }

  // Find the matching route
  const matchedRoute = Object.keys(ROUTE_LABELS).find((route) => {
    if (route === pathname) return true;
    // For dynamic routes, check if pathname starts with route
    if (pathname.startsWith(route + "/")) return true;
    return false;
  });

  if (matchedRoute) {
    // Add parent group if exists
    const parentGroup = GROUP_PARENTS[matchedRoute];
    if (parentGroup) {
      items.push({ text: parentGroup });
    }

    // Add current page
    const label = ROUTE_LABELS[matchedRoute];
    if (matchedRoute === pathname) {
      // Exact match - current page (no link)
      items.push({ text: label });
    } else {
      // Dynamic route - add base route as link
      items.push({ text: label, path: matchedRoute });

      // Add dynamic segment
      const dynamicSegment = pathname.slice(matchedRoute.length + 1);
      if (dynamicSegment) {
        // Format the dynamic segment (e.g., "123" -> "Account 123")
        const formattedSegment = dynamicSegment
          .split("/")
          .map((seg) => {
            // If it's a UUID or numeric ID, keep it simple
            if (/^[0-9a-f-]{36}$/.test(seg) || /^\d+$/.test(seg)) {
              return `Record ${seg.slice(0, 8)}`;
            }
            // Otherwise capitalize first letter
            return seg.charAt(0).toUpperCase() + seg.slice(1);
          })
          .join(" / ");

        items.push({ text: formattedSegment });
      }
    }
  } else {
    // Fallback: split by path and create breadcrumbs
    const segments = pathname.split("/").filter(Boolean);
    let currentPath = "";

    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      // Format segment label
      const label = segment
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      const isLast = index === segments.length - 1;
      items.push({
        text: label,
        path: isLast ? undefined : currentPath,
      });
    });
  }

  return items;
}

/** Breadcrumbs component with auto-generation from route. */
export default function Breadcrumbs({ items }: BreadcrumbsProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-generate breadcrumbs if not provided
  const breadcrumbItems = items || generateBreadcrumbs(location.pathname);

  const handleNavigate = (path?: string) => {
    if (path) {
      navigate(path);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <MuiBreadcrumbs
        aria-label="breadcrumb"
        separator="/"
        sx={{
          "& .MuiBreadcrumbs-separator": {
            mx: 1,
          },
        }}
      >
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;

          // First item gets home icon
          if (index === 0) {
            return isLast ? (
              <Typography
                key="home"
                sx={{ display: "flex", alignItems: "center" }}
                color="text.primary"
              >
                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                {item.text}
              </Typography>
            ) : (
              <Link
                key="home"
                underline="hover"
                sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                color="inherit"
                onClick={() => handleNavigate(item.path)}
                aria-label="Home"
              >
                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                {item.text}
              </Link>
            );
          }

          // Last item (current page) - not clickable
          if (isLast || !item.path) {
            return (
              <Typography key={index} color="text.primary">
                {item.text}
              </Typography>
            );
          }

          // Middle items - clickable links
          return (
            <Link
              key={index}
              underline="hover"
              sx={{ cursor: "pointer" }}
              color="inherit"
              onClick={() => handleNavigate(item.path)}
            >
              {item.text}
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
}
